import api from '../../../services/api';

/**
 * GET /api/orders/:id/stock-status (backend services/orderStockStatusService.js,
 * decision 2026-10-04 pkt 4–6). Quantities only — never a price. `onHand` =
 * stock on hand ("dostępność = stan na magazynie");
 * `supplierDraft.supplierOrderId` is null for users who cannot open a ZD.
 */
export type OrderStockLineState = 'AVAILABLE' | 'MISSING' | 'ISSUED' | 'UNKNOWN';
export type OrderSupplierDraftState = 'NONE' | 'PENDING' | 'REVIEWED' | 'REJECTED';

export interface OrderStockLine {
  orderProductId: string;
  name: string;
  unit: string;
  productVariantId: string | null;
  inCatalog: boolean;
  ordered: number | null;
  /** Already issued by WZ (ordered − open): Zamówiono = Wydano + Na stanie/Brakuje. */
  issued: number | null;
  onHand: number | null;
  available: number | null;
  missing: number | null;
  state: OrderStockLineState;
  customerText: string;
}

/** A truck carrying the draft ZD: no freight. `toCustomer` = it also carries this ZK (Fabryka → klient). */
export interface OrderDraftTransport {
  label: string | null;
  company: string | null;
  loadingDate: string | null;
  departureDate: string | null;
  status: string;
  toCustomer: boolean;
}

/** A ZK line live ZD buy only in part (decision 2026-10-19-podzial-pozycji-zk-na-dostawcow), in the line unit. */
export interface OrderPartiallyOrderedLine {
  orderProductId: string; name: string; unit: string;
  orderedQuantity: number; coveredQuantity: number; remainingQuantity: number;
}

export interface OrderSupplierDraft {
  documents?: OrderSupplierDraft[];
  partialLines?: OrderPartiallyOrderedLine[];
  state: OrderSupplierDraftState;
  supplierOrderId?: string | null;
  supplierOrderNumber?: string | null;
  supplierOrderStatus?: string | null;
  createdAt?: string | null;
  createdByName?: string | null;
  reviewedAt?: string | null;
  reviewedByName?: string | null;
  rejectionReason?: string | null;
  /** Dates the ZD carries (no prices): the ZK card's schedule prefers them to its own fields. */
  expectedDelivery?: string | null;
  estimatedCompletionDate?: string | null;
  /** Trucks of the draft ZD (the transport of a „prosto z fabryki” order). */
  transports?: OrderDraftTransport[];
}

/**
 * A person the confirmation dialog offers as a separate card (owner
 * 2026-10-05): active ADMIN / OPERATIONS user, id + name + role only, ticked by
 * default per the WP4 settings. Empty for a reader outside the dialog audience.
 */
export type NoticeRecipientRole = 'ADMIN' | 'OPERATIONS';
export interface OrderNoticeRecipient {
  userId: string;
  name: string;
  role: NoticeRecipientRole;
  defaultSelected: boolean;
}

export interface OrderStockStatus {
  orderId: string;
  orderNumber: string;
  status: string;
  participates: boolean;
  /** Planned as if confirmed now (the order is not in the allocation yet). */
  preview: boolean;
  lines: OrderStockLine[];
  missingLineCount: number;
  supplierDraft: OrderSupplierDraft;
  /** statusEmailsEnabled = the customer's opt-in for order e-mails. */
  customerContact: { name: string | null; email: string | null; statusEmailsEnabled: boolean };
  noticeDefaults: { customer: boolean; operations: boolean; admins: boolean };
  noticeRecipients: OrderNoticeRecipient[];
}

const asNumberOrNull = (value: unknown): number | null => {
  const number = typeof value === 'number' ? value : value == null ? NaN : Number(value);
  return Number.isFinite(number) ? number : null;
};
const asStringOrNull = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);
const LINE_STATES: OrderStockLineState[] = ['AVAILABLE', 'MISSING', 'ISSUED', 'UNKNOWN'];
const DRAFT_STATES: OrderSupplierDraftState[] = ['NONE', 'PENDING', 'REVIEWED', 'REJECTED'];

export function normalizeOrderStockStatus(raw: any): OrderStockStatus {
  const lines: OrderStockLine[] = Array.isArray(raw?.lines) ? raw.lines.map((line: any) => ({
    orderProductId: String(line?.orderProductId ?? ''),
    name: String(line?.name ?? ''),
    unit: String(line?.unit ?? 'pcs'),
    productVariantId: asStringOrNull(line?.productVariantId),
    inCatalog: line?.inCatalog === true,
    ordered: asNumberOrNull(line?.ordered),
    issued: asNumberOrNull(line?.issued),
    onHand: asNumberOrNull(line?.onHand),
    available: asNumberOrNull(line?.available),
    missing: asNumberOrNull(line?.missing),
    state: LINE_STATES.includes(line?.state) ? line.state : 'UNKNOWN',
    customerText: String(line?.customerText ?? ''),
  })) : [];
  const draft = raw?.supplierDraft || {};
  return {
    orderId: String(raw?.orderId ?? ''),
    orderNumber: String(raw?.orderNumber ?? ''),
    status: String(raw?.status ?? ''),
    participates: raw?.participates === true,
    preview: raw?.preview === true,
    lines,
    missingLineCount: lines.filter((line) => line.state === 'MISSING').length,
    supplierDraft: {
      documents: Array.isArray(draft.documents) ? draft.documents.map((document: any) => ({
        state: DRAFT_STATES.includes(document.state) ? document.state : 'REVIEWED',
        supplierOrderId: document.supplierOrderId || null,
        supplierOrderNumber: document.supplierOrderNumber || null,
        supplierOrderStatus: document.supplierOrderStatus || null,
      })) : undefined,
      partialLines: Array.isArray(draft.partialLines) ? draft.partialLines.map((line: any) => ({
        orderProductId: String(line?.orderProductId ?? ''),
        name: String(line?.name ?? ''),
        unit: String(line?.unit ?? 'pcs'),
        orderedQuantity: Number(line?.orderedQuantity ?? 0),
        coveredQuantity: Number(line?.coveredQuantity ?? 0),
        remainingQuantity: Number(line?.remainingQuantity ?? 0),
      })) : [],
      state: DRAFT_STATES.includes(draft.state) ? draft.state : 'NONE',
      supplierOrderId: asStringOrNull(draft.supplierOrderId),
      supplierOrderNumber: asStringOrNull(draft.supplierOrderNumber),
      supplierOrderStatus: asStringOrNull(draft.supplierOrderStatus),
      createdAt: asStringOrNull(draft.createdAt),
      createdByName: asStringOrNull(draft.createdByName),
      reviewedAt: asStringOrNull(draft.reviewedAt),
      reviewedByName: asStringOrNull(draft.reviewedByName),
      rejectionReason: asStringOrNull(draft.rejectionReason),
      expectedDelivery: asStringOrNull(draft.expectedDelivery),
      estimatedCompletionDate: asStringOrNull(draft.estimatedCompletionDate),
      transports: Array.isArray(draft.transports)
        ? draft.transports.map((truck: any): OrderDraftTransport => ({
          label: asStringOrNull(truck?.label),
          company: asStringOrNull(truck?.company),
          loadingDate: asStringOrNull(truck?.loadingDate),
          departureDate: asStringOrNull(truck?.departureDate),
          status: String(truck?.status ?? ''),
          toCustomer: truck?.toCustomer === true,
        }))
        : [],
    },
    customerContact: {
      name: asStringOrNull(raw?.customerContact?.name),
      email: asStringOrNull(raw?.customerContact?.email),
      statusEmailsEnabled: raw?.customerContact?.statusEmailsEnabled === true,
    },
    noticeDefaults: {
      customer: raw?.noticeDefaults?.customer === true,
      operations: raw?.noticeDefaults?.operations === true,
      admins: raw?.noticeDefaults?.admins === true,
    },
    noticeRecipients: Array.isArray(raw?.noticeRecipients)
      ? raw.noticeRecipients
        .filter((person: any) => typeof person?.userId === 'string' && (person.role === 'ADMIN' || person.role === 'OPERATIONS'))
        .map((person: any): OrderNoticeRecipient => ({
          userId: person.userId,
          name: String(person.name ?? ''),
          role: person.role,
          defaultSelected: person.defaultSelected === true,
        }))
      : [],
  };
}

export async function fetchOrderStockStatus(orderId: string): Promise<OrderStockStatus> {
  const response = await api.get(`/orders/${orderId}/stock-status`);
  return normalizeOrderStockStatus(response.data);
}
